import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { authSelector } from '../../features/auth/slice';
import axios from 'axios';
import { Search, Plus, MessageCircle, Lock, Pin, Paperclip, List, Megaphone, Briefcase } from 'lucide-react';
import Pagination from '../../components/common/Pagination';
import PublicLayout from '../../components/public/PublicLayout';
import { PUBLIC_BASE } from './config';
import UserAvatar from '../../components/UserAvatar';
import ProfilePopover from '../../components/ProfilePopover';

const BOARD_LABELS = {
  PNotice: '공지사항',
  PFree: '자유게시판',
};

const BOARD_PATHS = {
  PNotice: 'notice',
  PFree: 'free',
};

function PublicBoardListPage({ boardType = 'PNotice' }) {
  const { me } = useSelector(authSelector.selectAuthState);
  const navigate = useNavigate();

  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const limit = 15;

  const boardPath = BOARD_PATHS[boardType];

  useEffect(() => {
    const fetchArticles = async () => {
      let url = '/api/public/board';
      const params = { page: currentPage, limit, boardType };
      if (selectedCategory) {
        url = '/api/public/board/category';
        params.query = selectedCategory;
      } else if (searchQuery) {
        url = '/api/public/board/search';
        params.query = searchQuery;
      }
      try {
        const res = await axios.get(url, { params, withCredentials: true });
        setArticles(res.data.data || []);
        setTotalArticles(res.data.total);
        setTotalPages(res.data.totalPages);
      } catch (err) {
        console.error('게시글 로딩 실패:', err);
      }
    };
    fetchArticles();
  }, [currentPage, selectedCategory, searchQuery, boardType]);

  useEffect(() => {
    axios
      .get('/api/public/board/category/load', {
        params: { boardType },
        withCredentials: true,
      })
      .then((res) => setCategories(res.data))
      .catch(console.error);
  }, [boardType]);

  const handleSearchSubmit = () => {
    setSearchQuery(searchText);
    setSelectedCategory('');
    setCurrentPage(1);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setSearchText('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchText('');
    setSearchQuery('');
    setSelectedCategory('');
    setCurrentPage(1);
  };

  const canWrite = () => {
    if (!me) return false;
    if (boardType === 'PNotice') return me.adminYn === 'Y';
    return true; // PFree: all logged-in users
  };

  const handleTitleClick = (article) => {
    const isSecret = article.secretYn === 'Y';
    const isOwner =
      (me?.userType === 'client' && me?.id === Number(article.clientId)) ||
      (me?.userType === 'member' && me?.id === Number(article.memberId));
    const isAdmin = me?.userType === 'client' && me?.adminYn === 'Y';

    if (isSecret && !(isOwner || isAdmin)) {
      alert('비밀글입니다.');
      return;
    }

    navigate(`${PUBLIC_BASE}/community/${boardPath}/view/${article.id}`);
  };

  const displayNumber = (index) =>
    totalArticles - (currentPage - 1) * limit - index;

  const isNew = (createdAt) =>
    new Date() - new Date(createdAt) < 24 * 60 * 60 * 1000;

  const getAuthorInfo = (article) => {
    if (article.client) {
      return {
        userId: article.client.id,
        name: article.client.nickName || article.client.name,
        profileImage: article.client.profileImage,
        userType: 'client',
        adminYn: article.client.adminYn,
      };
    }
    if (article.member) {
      return {
        userId: article.member.id,
        name: article.member.nickName || article.member.name,
        profileImage: article.member.profileImage,
        userType: 'member',
        adminYn: 'N',
      };
    }
    return { userId: null, name: article.writer || '알 수 없음', profileImage: null, userType: 'member', adminYn: 'N' };
  };

  const isFiltered = searchQuery || selectedCategory;

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* 페이지 헤더 */}
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight text-center">
            커뮤니티
          </h1>
          <p className="text-center text-slate-500 text-sm mt-1">
            {boardType === 'PNotice' ? '공지사항과 최신 뉴스를 확인하세요.' : '자유롭게 소통하는 공간입니다.'}
          </p>
          {/* 탭 */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <Link
              to={`${PUBLIC_BASE}/community/notice`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                boardType === 'PNotice'
                  ? 'bg-sky-500/20 text-sky-400 border border-sky-500/30'
                  : 'bg-white/[0.04] text-slate-400 border border-white/[0.08] hover:bg-white/[0.08]'
              }`}>
              <Megaphone size={14} />
              공지사항
            </Link>
            <Link
              to={`${PUBLIC_BASE}/community/support`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors bg-white/[0.04] text-slate-400 border border-white/[0.08] hover:bg-white/[0.08]">
              <Briefcase size={14} />
              지원사업
            </Link>
            <Link
              to={`${PUBLIC_BASE}/community/free`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                boardType === 'PFree'
                  ? 'bg-sky-500/20 text-sky-400 border border-sky-500/30'
                  : 'bg-white/[0.04] text-slate-400 border border-white/[0.08] hover:bg-white/[0.08]'
              }`}>
              <MessageCircle size={14} />
              자유게시판
            </Link>
          </div>
        </div>

        {/* 툴바 */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400 whitespace-nowrap">
              총 <span className="text-sky-400 font-semibold">{totalArticles}</span>건
            </span>
            {categories.length > 1 && (
              <select
                className="px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200 text-sm outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent transition"
                onChange={handleCategoryChange}
                value={selectedCategory}>
                <option value="">전체 카테고리</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.category}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="flex items-center gap-2 flex-1 sm:max-w-sm sm:ml-auto">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              <input
                type="text"
                placeholder="검색어를 입력하세요"
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200 placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent transition"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
              />
            </div>
            <button
              onClick={handleSearchSubmit}
              className="shrink-0 px-4 py-2 rounded-xl bg-white/[0.03] border border-white/[0.08] text-slate-400 text-sm font-medium hover:bg-white/5 hover:text-white transition-colors">
              검색
            </button>
          </div>
        </div>

        {/* 필터 상태 */}
        {isFiltered && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs text-slate-500">
              {searchQuery && `"${searchQuery}" 검색 결과`}
              {selectedCategory && categories.find(c => c.id.toString() === selectedCategory)?.category && `"${categories.find(c => c.id.toString() === selectedCategory).category}" 필터`}
            </span>
            <button
              onClick={handleReset}
              className="text-xs text-sky-400 hover:text-sky-300 transition-colors">
              초기화
            </button>
          </div>
        )}

        {/* 게시글 테이블 */}
        <div className="overflow-x-auto rounded-2xl border border-white/[0.08] shadow-sm">
          <table className="w-full table-auto text-sm">
            <thead>
              <tr className="bg-white/[0.03] border-b border-white/[0.08]">
                <th className="px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide text-center w-16">
                  번호
                </th>
                {categories.length > 1 && (
                  <th className="px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide text-center hidden sm:table-cell w-24">
                    카테고리
                  </th>
                )}
                <th className="px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide text-left">
                  제목
                </th>
                <th className="px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide text-center w-24">
                  작성자
                </th>
                <th className="px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide text-center hidden sm:table-cell w-28">
                  작성일
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#182334] divide-y divide-white/[0.05]">
              {articles.length === 0 ? (
                <tr>
                  <td colSpan={categories.length > 1 ? 5 : 4} className="py-16 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <MessageCircle size={32} className="text-slate-600" />
                      <p className="text-slate-500 text-sm">게시글이 없습니다.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                articles.map((article, index) => {
                  const author = getAuthorInfo(article);
                  return (
                    <tr
                      key={article.id}
                      className={`transition-colors cursor-pointer ${
                        article.isNotice
                          ? 'bg-sky-500/[0.06] hover:bg-sky-500/[0.1]'
                          : 'hover:bg-white/[0.03]'
                      }`}
                      onClick={() => handleTitleClick(article)}>
                      <td className="px-3 py-3 text-center text-slate-500 text-xs">
                        {article.isNotice ? (
                          <span className="inline-flex items-center gap-1 text-sky-400 font-semibold">
                            <Pin size={12} />
                          </span>
                        ) : (
                          displayNumber(index)
                        )}
                      </td>
                      {categories.length > 1 && (
                        <td className="px-3 py-3 text-center hidden sm:table-cell">
                          <span className="inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium border-sky-500/20 bg-sky-500/10 text-sky-400">
                            {article.categoryName}
                          </span>
                        </td>
                      )}
                      <td className="px-4 py-3 text-left">
                        <div className="flex items-start gap-1.5 min-w-0">
                          {article.secretYn === 'Y' && (
                            <Lock size={13} className="text-slate-500 shrink-0 mt-0.5" />
                          )}
                          <span
                            className={`text-sm ${
                              article.isNotice
                                ? 'font-semibold text-sky-300'
                                : 'text-slate-200 hover:text-white'
                            }`}>
                            {article.title}
                          </span>
                          <div className="flex items-center gap-1 shrink-0 mt-0.5">
                            {article.commentCount > 0 && (
                              <span className="inline-flex items-center gap-0.5 text-[11px] text-sky-400">
                                <MessageCircle size={11} />
                                {article.commentCount}
                              </span>
                            )}
                            {article.fileCount > 0 && (
                              <Paperclip size={11} className="text-slate-500" />
                            )}
                            {isNew(article.createdAt) && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-500/20 text-rose-400">
                                N
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td
                        className="px-3 py-3 text-center"
                        onClick={(e) => e.stopPropagation()}>
                        {author.userId ? (
                          <ProfilePopover user={author}>
                            <UserAvatar
                              name={author.name}
                              profileImage={author.profileImage}
                              userType={author.userType}
                              adminYn={author.adminYn}
                              size={20}
                              showRole={true}
                            />
                            <span className="text-slate-400 text-xs truncate max-w-[80px] hover:text-sky-400 transition-colors">
                              {author.name}
                            </span>
                          </ProfilePopover>
                        ) : (
                          <div className="flex items-center justify-center gap-1.5">
                            <UserAvatar
                              name={author.name}
                              profileImage={author.profileImage}
                              userType={author.userType}
                              adminYn={author.adminYn}
                              size={20}
                              showRole={true}
                            />
                            <span className="text-slate-400 text-xs truncate max-w-[80px]">
                              {author.name}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center text-slate-500 text-xs hidden sm:table-cell">
                        {new Date(article.createdAt).toLocaleDateString('ko-KR')}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />

        {/* 하단 버튼 */}
        <div className="flex items-center justify-end mt-5 gap-2">
          {isFiltered && (
            <button
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-600 hover:bg-slate-500 text-white text-sm font-semibold transition-colors"
              onClick={handleReset}>
              <List size={14} />
              전체목록
            </button>
          )}
          {canWrite() && (
            <button
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold transition-colors"
              onClick={() => navigate(`${PUBLIC_BASE}/community/${boardPath}/write`)}>
              <Plus size={14} />
              글쓰기
            </button>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}

export default PublicBoardListPage;
